import {DateTime} from 'luxon';

import {tryFindDoNotDisturbInterval} from '../../../helpers/models/doNotDisturbHelpers';
import {AppSelector, createDeepEqualSelector} from '../../../helpers/reselectHelpers';
import {getDoNotDisturb, getShouldMuteNotifications} from '../preferences/preferencesSelectors';
import {getUser} from './teammatesSelectors';

/*
 * Types.
 */

export enum AvailabilityStatusTypesEnum {
  DEFAULT = 'DEFAULT',
  OUT_OF_OFFICE = 'OUT_OF_OFFICE',
  MUTED = 'MUTED',
  DO_NOT_DISTURB = 'DO_NOT_DISTURB',
}

interface BaseAvailabilityStatus {
  readonly type: AvailabilityStatusTypesEnum;
  readonly until?: DateTime | undefined;
}

interface ManualAvailabilityStatus extends BaseAvailabilityStatus {
  readonly type:
    | AvailabilityStatusTypesEnum.DEFAULT
    | AvailabilityStatusTypesEnum.OUT_OF_OFFICE
    | AvailabilityStatusTypesEnum.MUTED;
  readonly until?: undefined;
}

interface TimedAvailabilityStatus extends BaseAvailabilityStatus {
  readonly type: AvailabilityStatusTypesEnum.DEFAULT | AvailabilityStatusTypesEnum.DO_NOT_DISTURB;
  readonly until: DateTime;
}

export type AvailabilityStatus = ManualAvailabilityStatus | TimedAvailabilityStatus;
export type AvailabilityStatusFinder = (now: DateTime) => AvailabilityStatus;

/*
 * Constants.
 */

const manualDefaultAvailabilityStatus: AvailabilityStatus = Object.freeze({
  type: AvailabilityStatusTypesEnum.DEFAULT,
});

const mutedAvailabilityStatus: AvailabilityStatus = Object.freeze({
  type: AvailabilityStatusTypesEnum.MUTED,
});

const outOfOfficeAvailabilityStatus: AvailabilityStatus = Object.freeze({
  type: AvailabilityStatusTypesEnum.OUT_OF_OFFICE,
});

const findDefaultAvailabilityStatus: AvailabilityStatusFinder = () => manualDefaultAvailabilityStatus;
const findOutOfOfficeAvailabilityStatus: AvailabilityStatusFinder = () => outOfOfficeAvailabilityStatus;
const findMutedAvailabilityStatus: AvailabilityStatusFinder = () => mutedAvailabilityStatus;

/*
 * Selectors.
 */

/** Returns an availability status lookup function if DnD is enabled, or undefined otherwise. */
const getFindDoNotDisturbAvailabilityStatus: AppSelector<AvailabilityStatusFinder | undefined> =
  createDeepEqualSelector(getDoNotDisturb, (doNotDisturb) => {
    if (!doNotDisturb.isEnabled) {
      return undefined;
    }

    return (now) => {
      const interval = tryFindDoNotDisturbInterval(now, doNotDisturb);

      if (!interval) {
        return manualDefaultAvailabilityStatus;
      }

      return interval.contains(now)
        ? {
            type: AvailabilityStatusTypesEnum.DO_NOT_DISTURB,
            until: interval.end,
          }
        : {
            type: AvailabilityStatusTypesEnum.DEFAULT,
            until: interval.start,
          };
    };
  });

/** Returns an availability status lookup function. */
// Don't memoize, because the inputs change often, the computation is cheap, and the outputs are already memoized.
export const getFindAvailabilityStatus: AppSelector<AvailabilityStatusFinder> = (state) => {
  if (!getUser(state).isAvailable) {
    return findOutOfOfficeAvailabilityStatus;
  }

  if (getShouldMuteNotifications(state)) {
    return findMutedAvailabilityStatus;
  }

  const findDoNotDisturbAvailabilityStatus = getFindDoNotDisturbAvailabilityStatus(state);
  if (findDoNotDisturbAvailabilityStatus) {
    return findDoNotDisturbAvailabilityStatus;
  }

  return findDefaultAvailabilityStatus;
};
